import { STATE_LIST, TypeRequestEnum } from './enum';
import { Recording } from 'src/app/recordings/state/recording.model';
import { Attachment } from '../attachments/models/attachment.model';
import { Group } from 'src/app/contact-management/groups-management/models/group.model';
import { Contact, ContactExtended } from '../../contact-management/groups-management/models/contact.model';
import { EApiSyncCategory, ESyncPriority } from '../enums/sync';
import { IUser } from '../../user/user.model';
import { Territory } from '../types/territory';
import { ICurrency } from '../types/currency';
import { TOrder, TSortOption } from '../../playlist/enum';

export enum SyncTypeEnum {
  PSR = 'PSR',
  MSR = 'MSR',
  MLR = 'MLR',
  MCR = 'MCR',
  GPSR = 'GPSR',
  GMSR = 'GMSR',
}

export interface Label {
  id: number;
  fullTitle: string;
  shortTitle: string;
}

export interface Playlist {
  name: string;
  description: string;
  items: ItemDtos[];
  recordingIds: number[];
  sortOrder?: any;
  sortField?: any;
}

export interface ProjectPlaylistUpdateDTO {
  name: string;
  description: string;
}

export interface ProjectContactUpdateDTO {
  contactId: number;
  orderNum: number;
  notes?: string;
}

export interface ProjectUpdateDTO {
  brief: string;
  collaborationOpened: boolean;
  comments: string[];
  currencyId: number;
  currencyTerritoryId?: number;
  groups: Array<{ groupId: number; order: number }>;
  id: number;
  invoiceRecipientContactId: number;
  labels: Array<{ id: number; fullTitle: string; shortTitle: string }>;
  licenseManagerUserId: number;
  licenseeContactId: number;
  name: string;
  pitchTerritoryId: number;
  productionMusic: boolean;
  restriction: string;
  state: string;
  stepDeal: boolean;
  syncAuthorUserId: number;
  syncCategory: string;
  syncClientContactId: number;
  syncPriority: ESyncPriority;
  syncTerritoryId: number;
  syncType: SyncTypeEnum;

  licenseeContacts?: ProjectContactUpdateDTO[];
  invoiceRecipientContacts?: ProjectContactUpdateDTO[];
}

export interface ItemDtos {
  note: string;
  id: number;
  position: 0;
  recording: Recording;
  recordingStatus?: string;
  requestedTime?: string | null;
  playbackCount: number;
  downloadCount: number;

  addedToPlaylistBy?: string;
  addedToPlaylistAt?: any;
  parentalWarningType?: string;
  profitabilityScore?: number;
}

interface Tracking {
  activityName: string;
  updateTime: string;
  user: IUser | null;
}

export interface Collaborator {
  email: string;
  firstName: string;
  lastName: string;
}

interface ActiveRequestDto {
  id: number;
  recording: Recording;
  recordingId: number;
  status: string;
  type: TypeRequestEnum;
}

export interface PitchLink {
  expectedExpirationDate: string;
  externalPitchLink: string;
  globallyExpired: boolean;
  remainingViewsCount: number;
  expiredBy?: 'VIEWS' | 'DATE';
  expiredDate?: string;
}

export interface ProjectStateFlags {
  lockedFlag: boolean;
  pitchedFlag: boolean;
  playlistFlag: boolean;
  requestedFlag: boolean;
  message?: string;
}

export interface ProjectDeletePermission {
  canBeDeleted: boolean;
  message: string;
}

interface BaseProject {
  brief: string;
  productionMusic: boolean;
  clientNote: string;
  collaborationOpened: boolean;
  collaborators: Collaborator[];
  comments: string[];
  contacts: Contact[];
  creationTime: string;
  currencyTerritory?: Territory;
  description: string;
  deleted?: boolean;
  id: number;
  labels: Label[];
  name: string;
  notes: string[];
  state: STATE_LIST;
  syncAuthor?: IUser;
  licenseManager?: IUser;
  licensee?: Contact | ContactExtended | any;
  licensees?: Contact[] | ContactExtended[] | any;
  invoiceRecipient?: Contact | ContactExtended | any;
  invoiceRecipients?: Contact[] | ContactExtended[] | any;
  syncCategory: EApiSyncCategory;
  syncClient?: Contact;
  syncClients?: Contact[];
  syncTerritory: Territory;
  syncPriority?: ESyncPriority;
  pitchTerritory: Territory;
  updateTime: string | null;
  syncType: SyncTypeEnum;
  user: IUser;
  playlist?: Playlist;
  lastTracking: Tracking | null;
  activeRequest: ActiveRequestDto;
  requestsCount: number;
  viewsCount: number;
  playbackCount: number;
  downloadCount: number;
  discoViewsCount?: number;
  discoPlaybackCount?: number;
  discoDownloadCount?: number;
  showAlbumsArt: boolean;
  pitchLinks: PitchLink[];
  restriction?: string;
  locked?: boolean;
  hasWritePermission?: boolean;
  hasPlaylistWritePermission?: boolean;
  pitched?: boolean;
  stepDeal?: boolean;
  hasLockedInvoice: boolean;
  sharpId?: number;
  sharpGroupId?: number;
  currency?: ICurrency;
  projectStateFlags?: ProjectStateFlags;

  canDelete?: boolean;
  deletedBy?: IUser;

  /** GLSS-8042 */
  readonly?: boolean;
  mergedProject?: boolean;
  canUndoMerge?: true;
}

interface ProjectAdditional {
  songs?: Recording[];
  songCount?: number;
  hasRequests?: boolean;
  hasHiResolutionAudio?: boolean;
  hasMp3?: boolean;
  attachments?: Attachment[];
  groupCollaborators?: Collaborator[];
  songsPager?: {
    limit: number;
    offset: number;
    /**
     * Hydration invariant for playlist songs:
     * - `total` is a number (>= 0) only when playlist items were hydrated from the playlist-items API
     * - `total` is `undefined` when hydration is required (songs list is not loaded / was invalidated)
     */
    total?: number;
    sortField?: TSortOption;
    sortOrder?: TOrder;
  };
  groups?: Group[];

  productionMusic?: boolean;
  rightIds?: any[];
}

export type Project = BaseProject & ProjectAdditional;

export interface ProjectSearch {
  syncType: any;
  creationTime: string;
  id: number;
  title: string;
  productionTitle: string;
  user: string;
  lastActivity: string;
  updateTime: string;
  countPlaylistRecordings: number;
  hasActiveRequest: boolean;
  requestHasRecording: boolean;
  requestHasRecordingChanged: boolean;
  playlistHasRecording: boolean;
  recordingIds?: number[];
  restriction?: string;
  hasWritePermission?: boolean;
  pitched?: boolean;
  locked?: boolean;
  groups?: Group[];
}

export interface PlaylistItemsResponse {
  content: ItemDtos[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  pageable: any;
  size: number;
  sort: any;
  totalElements: number;
  totalPages: number;
}

export interface PlaylistItemsParams {
  artist?: string;
  songTitle?: string;
  writer?: string;
  offset?: number;
  limit?: number;
  status?: 'REQUESTED' | 'LICENSED' | undefined;
  dropExisting?: boolean;
  sortField?: TSortOption;
  sortOrder?: TOrder;
}

export interface IPlaylistSongsPager {
  songs?: Recording[];
  limit: number;
  offset: number;
  total: number;
  sortField?: TSortOption;
  sortOrder?: TOrder;
}

export interface IPitchHistory {
  manual: boolean;
  sentDate: string;
}
